import {Button} from 'antd';
import {FormInstance} from 'antd/es/form';
import React, {Dispatch, SetStateAction} from 'react';

import {FactorInfluenceInfoItemModalOpener, FactorInfluenceInfoServiceModalOpener} from 'components/ais/factor-influence-info';
import {ButtonOpenLink} from 'components/directory/action/button-open-link/button-open-link';
import {TIconType} from 'components/dynamic-icon';
import {ButtonHistoryBack} from 'components/form/actions/button-history-back';
import {BlockUserButton} from 'components/form/actions/custom/administration-accounts-fns/block-user-button';
import {CollapseButton} from 'components/request/actions/collapse-button';
import {DrillDownActionLink} from 'components/table/action';
import {OpenForm} from 'components/table/action/open-form/open-form';
import {TableOpenModalAction} from 'components/table/action/open-modal';
import {IMetaActionInfo, EMetaActionType, IUrlConfig} from 'entities/metadata';
import {ButtonActionCustomLink} from 'features/provide-custom-link';
import {ButtonForCFT} from 'features/provide-microservice-cft';

import {ButtonBack} from './actions/button-back/button-back';
import {ButtonChangeRequestStatus} from './actions/button-change-request-status';
import {ButtonCleanForm} from './actions/button-clean-form';
import {ButtonClearForm} from './actions/button-clear-form/button-clear-form';
import {ButtonConfirmSave} from './actions/button-confirm-save/button-confirm-save';
import {ButtonDeleteElement} from './actions/button-delete-element/button-delete-element';
import {ButtonDownloadDocumentFile} from './actions/button-download-document-file/button-download-document-file';
import {ButtonDownloadFile} from './actions/button-download-file/button-download-file';
import {ButtonEditForm} from './actions/button-edit-form/button-edit-form';
import {ButtonOpenChildModal} from './actions/button-open-child-modal/button-open-child-modal';
import {ButtonOpenModal} from './actions/button-open-modal';
import {ButtonOpenModalUsingParams} from './actions/button-open-modal-using-params/button-open-modal-using-params';
import {ButtonPublishDocuments} from './actions/button-publish-documents';
import {ButtonRemoveFilter} from './actions/button-remove-filter';
import {ButtonSave} from './actions/button-save';
import {ButtonSaveDraft} from './actions/button-save-draft';
import {ButtonSaveOrEditForm} from './actions/button-save-or-edit-form';
import {ButtonSendToAgreement} from './actions/button-send-to-agreement';
import {ButtonSetInitialData} from './actions/button-set-initial-data/button-set-initial-data';
import {CheckboxSimple} from './inputs/checkbox-simple/checkbox-simple';

/**
 * Функция для получения нужного компонента для действия
 */
export const actionsTypeResolver = (
    actionMeta: IMetaActionInfo,
    form?: FormInstance,
    onClose?: () => void,
    setFormErrorMessage?: Dispatch<SetStateAction<string | null>>,
    onBack?: () => void,
    onClearForm?: () => void,
    formMode?: string,
    handleOpenConfirmModal?: () => void,
    urlConfig?: IUrlConfig,
) => {
    const {
        actionType,
        entityName,
        linkedEntityName,
        ...action
    } = actionMeta;
    switch (actionType) {
    case EMetaActionType.SIMPLE_CHECKBOX:
        return () => (
            <CheckboxSimple
                name={action.actionCode}
                label={action.actionTitle}
            />
        );
    case EMetaActionType.BUTTON_OPEN_LINK:
        return () => (
            <ButtonOpenLink
                entityName={entityName || ''}
                meta={actionMeta}
            />
        );
    case EMetaActionType.FILTER:
        return (props: any) => (
            <TableOpenModalAction
                meta={actionMeta}
                entityName={entityName}
                {...props}
            />
        );
    case EMetaActionType.LAUNCH_PROGRAM:
    case EMetaActionType.BUTTON_SAVE_FORM:
    case EMetaActionType.BUTTON_SEND_FILTER:
    case EMetaActionType.BUTTON_SEND_FOR_APPROVAL_REQUEST:
    case EMetaActionType.BUTTON_SEND_COMMENT_ON_THE_REQUEST:
        return (props: any) => {
            const {actionTitle = 'Сохранить', actionIcon, buttonType} = action;

            return (
                <ButtonSave
                    {...props}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    actionType={actionType}
                    buttonType={buttonType}
                />
            );
        };
    case EMetaActionType.BUTTON_FOR_CFT:
        return (props: any) => {
            const {
                actionTitle = 'Сохранить',
                actionIcon,
                buttonType,
                referenceUrl,
            } = action;

            return (
                <ButtonForCFT
                    {...props}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    actionType={actionType}
                    buttonType={buttonType}
                    url={referenceUrl}
                    form={form}
                    urlConfig={urlConfig}
                    onClose={onClose}
                />
            );
        };
    case EMetaActionType.BUTTON_SEND_FOR_APPROVAL_REQUEST_DRAFT:
    case EMetaActionType.BUTTON_SAVE_FORM_DRAFT:
        return (props: any) => {
            const {
                actionTitle = 'Сохранить',
                actionIcon,
                actionCode,
                referenceUrl,
                requestType,
            } = action;
            return (
                <ButtonSaveDraft
                    {...props}
                    actionType={actionType}
                    actionTitle={actionTitle}
                    entityName={action.parentEntityName}
                    actionIcon={actionIcon}
                    url={referenceUrl}
                    requestType={requestType}
                    actionCode={actionCode}
                    form={form}
                    onClose={onClose}
                />
            );
        };
    case EMetaActionType.BUTTON_SEND_TO_AGREEMENT:
        return (props: any) => {
            const {
                actionTitle = 'Сохранить',
                actionIcon,
                actionCode,
                referenceUrl,
                requestType,
            } = action;
            return (
                <ButtonSendToAgreement
                    {...props}
                    actionTitle={actionTitle}
                    actionType={actionType}
                    actionIcon={actionIcon}
                    url={referenceUrl}
                    requestType={requestType}
                    actionCode={actionCode}
                    onClose={onClose}
                />
            );
        };
    case EMetaActionType.PUBLISH_LIST:
        return (props: any) => {
            const {
                actionTitle = 'Сохранить',
                actionIcon,
                actionCode,
                referenceUrl,
                requestType,
                parentEntityName,
            } = action;
            return (
                <ButtonPublishDocuments
                    {...props}
                    actionTitle={actionTitle}
                    parentEntityName={parentEntityName}
                    actionIcon={actionIcon}
                    referenceUrl={referenceUrl}
                    requestType={requestType}
                    actionCode={actionCode}
                    form={form}
                />
            );
        };
    case EMetaActionType.BUTTON_DOWNLOAD_FILE:
        return (props: any) => {
            const {
                actionTitle = 'Сохранить',
                actionIcon,
                referenceUrl,
                requestType,
            } = action;

            return (
                <ButtonDownloadFile
                    {...props}
                    actionTitle={actionTitle}
                    referenceUrl={referenceUrl}
                    requestType={requestType}
                    actionIcon={actionIcon}
                    form={form}
                    onClose={onClose}
                    setFormErrorMessage={setFormErrorMessage}
                    linkedEntityName={linkedEntityName}
                />
            );
        };
    case EMetaActionType.BUTTON_DOWNLOAD_DOCUMENT_FILE:
        return (props: any) => {
            const {
                actionTitle = 'Сохранить',
                actionIcon,
                referenceUrl,
                requestType,
                mainReferenceUrl,
                parentEntityName,
            } = action;

            return (
                <ButtonDownloadDocumentFile
                    {...props}
                    actionTitle={actionTitle}
                    referenceUrl={referenceUrl}
                    requestType={requestType}
                    actionIcon={actionIcon}
                    form={form}
                    onClose={onClose}
                    setFormErrorMessage={setFormErrorMessage}
                    linkedEntityName={linkedEntityName}
                    mainReferenceUrl={mainReferenceUrl}
                    parentEntityName={parentEntityName}
                />
            );
        };
    case EMetaActionType.OPEN_MODAL:
        return (props: any) => {
            const {
                actionTitle = 'Открыть',
                actionIcon,
                modalName,
                modalEntityName,
                buttonType,
            } = action;

            return (
                <ButtonOpenModal
                    title={actionTitle}
                    icon={actionIcon}
                    modalName={modalName}
                    modalEntityName={modalEntityName}
                    buttonType={buttonType}
                    {...props}
                />
            );
        };
    case EMetaActionType.OPEN_FACTOR_INFLUENCE_INFO_ITEM_MODAL:
        return () => (
                <FactorInfluenceInfoItemModalOpener
                    meta={actionMeta}
                    buttonTitle={actionMeta.actionTitle}
                    entityName={entityName || ''}
                />
            );
    case EMetaActionType.OPEN_FACTOR_INFLUENCE_INFO_SERVICE_MODAL:
        return (props: any) => (
            <FactorInfluenceInfoServiceModalOpener
                meta={actionMeta}
                entityName={entityName || ''}
                {...props}
            />
        );
    case EMetaActionType.BUTTON_OPEN_MODAL_WITH_QUERY_PARAMS:
        return (props: any) => {
            const {
                actionTitle, actionIcon, linkField, modalEntityName, modalName,
            } = action;
            return (
                <ButtonOpenModalUsingParams
                    entityName={entityName}
                    title={actionTitle}
                    icon={actionIcon as TIconType}
                    linkField={linkField}
                    modalName={modalName}
                    modalEntityName={modalEntityName}
                    {...props}
                />
            );
        };
    case EMetaActionType.REMOVE_FILTER:
        return (props: any) => {
            const {
                actionTitle, parentEntityName,
            } = action;

            return (
                <ButtonRemoveFilter
                    actionTitle={actionTitle}
                    parentEntityName={parentEntityName}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_CHANGE_REQUEST_STATUS:
        return (props: any) => {
            const {
                actionTitle, actionIcon, linkField, actionCode,
            } = action;

            return (
                <ButtonChangeRequestStatus
                    entityName={entityName}
                    title={actionTitle}
                    icon={actionIcon}
                    linkField={linkField}
                    status={actionCode}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_BACK:
        return (props: any) => {
            const {
                actionTitle, actionIcon, buttonType,
            } = action;

            return (
                <ButtonBack
                    entityName={entityName}
                    type={buttonType}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    onClose={onClose}
                    onBack={onBack}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_HISTORY_BACK:
        return (props: any) => (
            <ButtonHistoryBack
                {...action}
                {...props}
            />
        );
    case EMetaActionType.BUTTON_SET_INITIAL_DATA:
        return (props: any) => {
            const {
                actionTitle, actionIcon, modalEntityName, buttonType,
            } = action;

            return (
                <ButtonSetInitialData
                    type={buttonType}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    entityName={modalEntityName}
                    form={form}
                    {...props}
                />
            );
        };

    case EMetaActionType.BUTTON_DELETE_ELEMENT:
        return (props: any) => {
            const {
                actionTitle,
                referenceUrl,
                requestType,
                linkField,
                actionIcon,
                useUrl,
                itemListKey,
                modalEntityName,
            } = action;

            return (
                <ButtonDeleteElement
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    entityName={modalEntityName}
                    referenceUrl={referenceUrl}
                    requestType={requestType}
                    linkField={linkField}
                    onClose={onClose}
                    useUrl={useUrl}
                    itemListKey={itemListKey}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_EDIT_FORM:
        return (props: any) => {
            const {
                actionTitle, actionIcon,
            } = action;
            return (
                <ButtonEditForm
                    entityName={action.modalEntityName}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_SAVE_OR_EDIT_FORM:
        return (props: any) => {
            const {buttonType} = action;
            return (
                <ButtonSaveOrEditForm
                    entityName={action.modalEntityName}
                    buttonType={buttonType}
                    formMode={formMode}
                    action={action}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_CONTROL_COLLAPSE:
        return (props: any) => {
            const {
                actionIcon,
            } = action;
            return (
                <CollapseButton
                    actionIcon={actionIcon}
                    {...props}
                />
            );
        };
    case EMetaActionType.CREATE_PROLONGATION_REQUEST_DRAFT:
        return (props: any) => {
            const {
                actionIcon,
            } = action;
            return (
                <CollapseButton
                    actionIcon={actionIcon}
                    {...props}
                />
            );
        };
    case EMetaActionType.OPEN_REQUESTS_CHILD_MODAL:
        return (props: any) => {
            const {
                actionTitle, actionIcon, modalName, modalEntityName, parentEntityName,
            } = action;
            return (
                <ButtonOpenChildModal
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    modalName={modalName}
                    modalEntityName={modalEntityName}
                    parentEntityName={parentEntityName}
                    {...props}
                />
            );
        };
    case EMetaActionType.BUTTON_CLEAR_FORM:
        return () => {
            const {actionTitle, actionIcon} = action;
            return (
                <ButtonClearForm
                    handleClearForm={onClearForm}
                    {...{actionTitle, actionIcon}}
                />
            );
        };
    case EMetaActionType.BUTTON_CLEAN_FORM:
        return (props: any) => {
            const {actionTitle = 'Сохранить', actionIcon, buttonType} = action;

            return (
                <ButtonCleanForm
                    {...props}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    actionType={actionType}
                    buttonType={buttonType}
                    entityName={entityName}
                    handleClearForm={onClearForm}
                    form={form}
                />
            );
        };
    case EMetaActionType.OPEN_FORM:
        return (props: any) => (
            <OpenForm
                entityName={entityName}
                {...props}
            />
        );
    case EMetaActionType.BUTTON_SAVE_FORM_PERSONAL_ACCOUNT:
        return (props: any) => {
            const {
                actionIcon, actionTitle,
            } = action;
            return (
                <ButtonConfirmSave
                    {...props}
                    actionTitle={actionTitle}
                    actionIcon={actionIcon}
                    handleOpenConfirmModal={handleOpenConfirmModal}
                />
            );
        };
    case EMetaActionType.BUTTON_BLOCK_USER:
        return () => (
            <BlockUserButton
                referenceUrl={action.referenceUrl}
                onClose={onClose}
            />
        );
    case EMetaActionType.CUSTOM_LINK:
        return (props: any) => (
            <ButtonActionCustomLink
                entityName={entityName}
                meta={actionMeta}
                {...props}
            />
        );
    case EMetaActionType.CUSTOM_LINK_TABLE_ROW:
        return (props: any) => (
            <DrillDownActionLink
                entityName={entityName}
                meta={actionMeta}
                {...props}
            />
        );
    default:
        return Button;
    }
};
